import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Type, Upload, Trash2, Info, CheckCircle } from 'lucide-react';
import CardBasic from '../../ui/Card';

/**
 * CustomFontsManager (#123b)
 * Verwaltung der eigenen Schriftart eines Mandanten fuer die oeffentlichen
 * Widgets (Anfrageformulare + Newsletter). Eine Schrift pro Mandant.
 */
const CustomFontsManager = () => {
    const [font, setFont] = useState(null);      // { exists, familyName, url, ... }
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [familyName, setFamilyName] = useState('');
    const fileRef = useRef(null);

    const styleElRef = useRef(null);

    // @font-face fuer die Vorschau injizieren (unabhaengig vom Seiten-Reload)
    const injectFontFace = useCallback((url) => {
        if (!styleElRef.current) {
            styleElRef.current = document.createElement('style');
            document.head.appendChild(styleElRef.current);
        }
        styleElRef.current.textContent =
            `@font-face{font-family:'FMCustomFontPreview';src:url('${url}?t=${Date.now()}');font-display:swap;}`;
    }, []);

    const loadFont = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch('index.php/settings/getCustomFont', { credentials: 'same-origin' });
            const data = await res.json();
            setFont(data);
            if (data.exists && data.url) injectFontFace(data.url);
        } catch (e) {
            setError('Fehler beim Laden der Schriftart');
        } finally {
            setLoading(false);
        }
    }, [injectFontFace]);

    useEffect(() => { loadFont(); }, [loadFont]);

    const handleUpload = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) {
            setError('Bitte zuerst eine Schriftdatei auswählen.');
            return;
        }
        setError('');
        setSuccess('');
        setUploading(true);
        try {
            const body = new FormData();
            body.append('font', file);
            body.append('familyName', familyName.trim());
            const res = await fetch('index.php/settings/uploadCustomFont', {
                method: 'POST', credentials: 'same-origin', body
            });
            const data = await res.json();
            if (data.success) {
                setSuccess('Schriftart erfolgreich hochgeladen.');
                setFamilyName('');
                if (fileRef.current) fileRef.current.value = '';
                await loadFont();
            } else {
                setError(data.error || 'Upload fehlgeschlagen.');
            }
        } catch (e) {
            setError('Upload fehlgeschlagen.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        setError('');
        setSuccess('');
        try {
            const res = await fetch('index.php/settings/deleteCustomFont', {
                method: 'POST', credentials: 'same-origin'
            });
            const data = await res.json();
            if (data.success) {
                setSuccess('Schriftart entfernt.');
                setFont({ exists: false });
            } else {
                setError('Löschen fehlgeschlagen.');
            }
        } catch (e) {
            setError('Löschen fehlgeschlagen.');
        }
    };

    return (
        <div className="container-fluid" style={{ maxWidth: '900px' }}>
            <a href="index.php/settings" style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                color: 'var(--text-secondary, #64748b)', textDecoration: 'none',
                fontSize: '0.875rem', marginBottom: '0.75rem'
            }}>
                <ArrowLeft size={16} /> Zurück zu den Einstellungen
            </a>

            <div className="d-flex align-items-center gap-2 mb-1">
                <Type size={32} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                <h1 className="h2 fw-bold text-dark mb-0">Eigene Schriftart</h1>
            </div>
            <p style={{ color: 'var(--text-secondary, #64748b)', marginBottom: '1.5rem' }}>
                Lade eine eigene Schrift hoch, um sie in deinen Anfrageformularen und im
                Newsletter-Anmeldeformular zu verwenden.
            </p>

            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success d-flex align-items-center gap-2"><CheckCircle size={16} /> {success}</div>}

            {loading ? (
                <p style={{ color: 'var(--text-secondary)' }}>Lädt…</p>
            ) : (
                <>
                    {font?.exists && (
                        <CardBasic className="mb-4">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
                                        Aktuelle Schrift
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: '1.1rem', marginTop: '0.15rem' }}>
                                        {font.familyName}
                                    </div>
                                    {font.originalName && (
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{font.originalName}</div>
                                    )}
                                    <div style={{ fontFamily: "'FMCustomFontPreview', sans-serif", fontSize: '1.6rem', marginTop: '0.75rem', color: 'var(--text-primary)' }}>
                                        Hallo! Das ist deine Schrift. 1234567890
                                    </div>
                                </div>
                                <button className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1" onClick={handleDelete}>
                                    <Trash2 size={16} /> Entfernen
                                </button>
                            </div>
                        </CardBasic>
                    )}

                    <CardBasic className="mb-4">
                        <h5 className="fw-bold mb-3">{font?.exists ? 'Schrift ersetzen' : 'Schrift hochladen'}</h5>
                        <div className="mb-3">
                            <label className="form-label">Anzeigename (optional)</label>
                            <input type="text" className="form-control" placeholder="z. B. Meine Hausschrift"
                                value={familyName} onChange={(e) => setFamilyName(e.target.value)} style={{ maxWidth: '360px' }} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Schriftdatei</label>
                            <input ref={fileRef} type="file" className="form-control" accept=".woff2,.woff,.ttf,.otf"
                                style={{ maxWidth: '360px' }} />
                            <small className="text-muted d-block mt-1">Formate: WOFF2, WOFF, TTF, OTF · max. 2 MB</small>
                        </div>
                        <button className="btn btn-outline-primary d-flex align-items-center gap-2" onClick={handleUpload} disabled={uploading}>
                            <Upload size={16} /> {uploading ? 'Wird hochgeladen…' : 'Hochladen'}
                        </button>
                    </CardBasic>

                    <CardBasic style={{ background: 'var(--bg-secondary, #f5f7f9)' }}>
                        <div className="d-flex gap-2">
                            <Info size={18} style={{ color: 'var(--accent-slate, #5b7c8d)', flexShrink: 0, marginTop: '2px' }} />
                            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                <strong>So verwendest du die Schrift:</strong> Wähle nach dem Hochladen in den
                                Einstellungen eines Formulars (Reiter „Darstellung") bzw. einer Newsletter-Gruppe
                                bei „Schriftart" den Eintrag <em>„Eigene Schrift"</em>.
                                <br /><br />
                                <strong>Lizenzhinweis:</strong> Lade nur Schriften hoch, für die du die nötigen
                                Nutzungsrechte besitzt. Die Schrift wird ausschließlich von deinem eigenen
                                Bereich ausgeliefert (kein externer Dienst).
                            </div>
                        </div>
                    </CardBasic>
                </>
            )}
        </div>
    );
};

export default CustomFontsManager;
